import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export interface Agent {
  _id?: string;
  name: string;
  description: string;
  siteUrl: string | string[];
  documents: string[];
  systemPrompt?: string;
}

export interface UploadResponse {
  fileId: string;
  originalName: string;
  size: number;
  mimeType: string;
}

export interface DocumentMetadata {
  fileId: string;
  displayName: string;
  filename: string;
  mimetype: string;
  size: number;
  uploadDate: string;
}

export const agentApi = {
  // Get all agents
  getAgents: async (): Promise<Agent[]> => {
    try {
      const response = await apiClient.get<Agent[]>('/agents');
      return response.data;
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  // Get a single agent
  getAgent: async (id: string): Promise<Agent> => {
    try {
      const response = await apiClient.get<Agent>(`/agents/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching agent ${id}:`, error);
      throw error;
    }
  },

  // Create a new agent with optional file uploads
  createAgent: async (agent: Agent, files?: File[]): Promise<Agent> => {
    try {
      // If no files, use the regular JSON API
      if (!files || files.length === 0) {
        const response = await apiClient.post<Agent>('/agents', agent);
        return response.data;
      }

      // Create form data for multipart request
      const formData = new FormData();

      // Add agent data
      formData.append('name', agent.name);
      formData.append('description', agent.description);

      // Handle siteUrl as either string or array
      if (Array.isArray(agent.siteUrl)) {
        agent.siteUrl.forEach((url) => {
          if (url) formData.append('siteUrl[]', url);
        });
      } else if (agent.siteUrl) {
        formData.append('siteUrl', agent.siteUrl);
      }

      // Add existing document IDs (if any)
      if (agent.documents && agent.documents.length > 0) {
        agent.documents.forEach((docId) => {
          formData.append('documents', docId);
        });
      }

      // Add files
      files.forEach((file) => {
        formData.append('documents', file);
      });

      // Make multipart request
      const response = await axios.post<Agent>(
        `${API_BASE_URL}/agents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  },

  // Update an agent with optional file uploads
  updateAgent: async (
    id: string,
    agent: Agent,
    files?: File[]
  ): Promise<Agent> => {
    try {
      // If no files, use the regular JSON API
      if (!files || files.length === 0) {
        const response = await apiClient.put<Agent>(`/agents/${id}`, agent);
        return response.data;
      }

      // Create form data for multipart request
      const formData = new FormData();

      // Add agent data
      formData.append('name', agent.name);
      formData.append('description', agent.description);

      // Handle siteUrl as either string or array
      if (Array.isArray(agent.siteUrl)) {
        agent.siteUrl.forEach((url) => {
          if (url) formData.append('siteUrl[]', url);
        });
      } else if (agent.siteUrl) {
        formData.append('siteUrl', agent.siteUrl);
      }

      // Add existing document IDs (if any)
      if (agent.documents && agent.documents.length > 0) {
        agent.documents.forEach((docId) => {
          formData.append('documents', docId);
        });
      }

      // Add files
      files.forEach((file) => {
        formData.append('documents', file);
      });

      // Make multipart request
      const response = await axios.put<Agent>(
        `${API_BASE_URL}/agents/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      return response.data;
    } catch (error) {
      console.error(`Error updating agent ${id}:`, error);
      throw error;
    }
  },

  // Delete an agent
  deleteAgent: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/agents/${id}`);
    } catch (error) {
      console.error(`Error deleting agent ${id}:`, error);
      throw error;
    }
  },

  // Upload a document (standalone endpoint)
  uploadDocument: async (formData: FormData): Promise<UploadResponse> => {
    try {
      // Create a custom instance for file upload with different content-type header
      const response = await axios.post<UploadResponse>(
        `${API_BASE_URL}/agents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // Get document metadata
  getDocumentMetadata: async (fileId: string): Promise<DocumentMetadata> => {
    try {
      const response = await apiClient.get<DocumentMetadata>(
        `/agents/document/${fileId}`
      );

      // Check if response contains the necessary fields
      if (!response.data.mimetype || !response.data.filename) {
        console.error('Incomplete metadata received:', response.data);
      }

      return response.data;
    } catch (error: any) {
      console.error(`Error fetching document metadata for ${fileId}:`, error);
      if (error.response) {
        console.error(
          `Error status: ${error.response.status}, data:`,
          error.response.data
        );
      }
      throw error;
    }
  },

  // Get document content (text format)
  getDocumentContent: async (fileId: string): Promise<string> => {
    try {
      // First get the metadata to check mimetype
      const metadata = await agentApi.getDocumentMetadata(fileId);

      // For PDF files, we'll not fetch the content in text format
      if (metadata.mimetype && metadata.mimetype.includes('pdf')) {
        return 'PDF content cannot be displayed. Please use the download option.';
      }

      const contentUrl = `${API_BASE_URL}/agents/document/${fileId}/content`;

      const response = await axios.get(contentUrl, {
        responseType:
          metadata.mimetype && metadata.mimetype.includes('text')
            ? 'text'
            : 'arraybuffer',
        withCredentials: true,
      });

      // Handle binary content if needed
      if (
        response.headers['content-type'] &&
        !response.headers['content-type'].includes('text')
      ) {
        // For binary content, return a placeholder message
        return `Binary content (${metadata.mimetype}) cannot be displayed directly. Please download the file.`;
      }

      return response.data as string;
    } catch (error: any) {
      console.error(`Error fetching document content for ${fileId}:`, error);
      if (error.response) {
        console.error(
          `Content error status: ${error.response.status}, data:`,
          error.response.data
        );
        if (error.response.status === 404) {
          return 'Document not found or has been deleted.';
        }
        return `Error: ${error.response.data?.message || error.message || 'Unknown error'}`;
      }
      return `Error loading document: ${error.message || 'Unknown error'}`;
    }
  },

  // Get document download URL
  getDocumentDownloadUrl: (fileId: string): string => {
    return `${API_BASE_URL}/agents/document/${fileId}/download`;
  },

  // Search for documents by content similarity
  searchDocuments: async (
    query: string,
    limit: number = 5
  ): Promise<DocumentMetadata[]> => {
    try {
      const response = await apiClient.get<DocumentMetadata[]>(
        `/agents/search/documents`,
        {
          params: { query, limit },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  },
};
